/* =============================================================================
 * ATRIBUCIÓN · que la campaña llegue viva hasta la tienda.
 * -----------------------------------------------------------------------------
 * EL PROBLEMA, Y POR QUÉ NO BASTA CON «pasar la query». Los `utm_*` llegan en la
 * URL de ATERRIZAJE y **sólo ahí**. Basta con que el visitante navegue del hub a
 * `/bogota` —o que pulse cualquier enlace interno— para que React Router los
 * borre de la barra de direcciones. Cuando por fin llega al botón de entradas ya
 * no existen, y el enlace a la tienda sale desnudo: pretix guarda el pedido sin
 * campaña y **la venta no se puede atribuir al anuncio que la pagó.**
 *
 * Por eso se capturan en cuanto la página monta y se guardan en `sessionStorage`.
 * SESIÓN, no `localStorage`, y es deliberado: una campaña pertenece a la visita.
 * Guardarla para siempre haría que una compra de dentro de tres semanas se
 * atribuyera a un anuncio que el comprador vio una vez — eso no es medir, es
 * inflar.
 *
 * QUÉ SE LLEVA: **TODO LO QUE TRAIGA LA URL**, no una lista cerrada.
 *
 * Aquí hubo una lista de nueve nombres —los cinco `utm_*` y cuatro
 * identificadores de clic— y el 12-ago-2026 se midió lo que costaba: de doce
 * parámetros que manda un anuncio real, **llegaban dos y se perdían diez**. Entre
 * los perdidos, `utm_id` —el que Ads Manager pone por defecto— y `ad_id`,
 * `adset_id` y `campaign_id`, que son con los que una agencia distingue qué
 * anuncio concreto vendió la entrada. Una lista cerrada envejece cada vez que una
 * plataforma inventa un parámetro, y envejece **en silencio**.
 *
 * Así que se propaga la query entera. Lo único que se filtra es lo que no debe
 * viajar nunca (ver `PROHIBIDOS`) y lo que es absurdamente grande.
 *
 * LO QUE NO HACE: inventarse una campaña. Si el visitante llegó escribiendo la
 * dirección, no hay nada que propagar y el enlace sale limpio.
 * ========================================================================== */

const CLAVE = 'az-campaign-v1';

/**
 * LA DIFERENCIA ENTRE ESTAS DOS COSAS ES LEGAL, NO TÉCNICA.
 *
 * Un `utm_*` —o un `ad_id`— describe **la campaña**: de qué anuncio viene la visita. No identifica a
 * nadie, no sale del dominio del cliente y muere con la pestaña, así que viaja siempre. Si esperara
 * al consentimiento, se perdería la atribución de todo el que rechace cookies, que es justo lo que
 * no puede pasar.
 *
 * Un `fbclid` o un `gclid` son otra cosa: **identificadores del clic**, que las plataformas usan
 * para reconocer a la persona al otro lado. Ésos sólo se guardan y se propagan **con
 * consentimiento**. La lista se amplía con las demás plataformas para no repetir el error de
 * quedarse corto: `gbraid`/`wbraid` (Google en iOS), `twclid`, `li_fat_id`, `epik`, `sccid`,
 * `dclid`, `yclid`, `igshid`.
 */
const CLICK_IDS = new Set([
  'fbclid', 'gclid', 'gbraid', 'wbraid', 'dclid', 'ttclid', 'msclkid', 'twclid', 'li_fat_id',
  'epik', 'sccid', 'yclid', 'igshid', 'rdt_cid', 'obclid', 'vmcid',
]);

/**
 * LO QUE NO SE PROPAGA NUNCA, aunque venga en la URL.
 *
 * Propagar la query entera es lo correcto para no perder atribución, pero un enlace se comparte por
 * WhatsApp, se pega en un grupo y acaba indexado. Si algún día alguien monta un enlace con un dato
 * de acceso dentro, no queremos reenviarlo a otro dominio. La comparación es sobre el nombre en
 * minúsculas y por CONTENER, no por igualdad: `auth_token` y `authToken` caen los dos.
 */
const PROHIBIDOS = [
  'password', 'passwd', 'contrasena', 'token', 'secret', 'apikey', 'api_key', 'auth', 'session',
  'jwt', 'otp', 'pin', 'cvv', 'iban', 'dni', 'nif',
  // Datos de la persona. Un anuncio no los manda; un enlace mal montado sí, y ese enlace se
  // comparte por WhatsApp. No se reenvían a otro dominio.
  'email', 'correo', 'mail', 'telefono', 'phone', 'movil',
];

/**
 * Topes de cordura. No son una opinión de diseño: son el freno a una URL construida a mano con
 * cientos de parámetros o con un valor de un megabyte, que llenaría `sessionStorage` y rompería la
 * navegación por algo que no es ni una campaña.
 */
const MAX_PARAMS = 40;
const MAX_LARGO = 512;

type Campana = Record<string, string>;

/** ¿Este parámetro puede viajar, y necesita el sí del visitante? */
function admisible(clave: string, conConsentimiento: boolean): boolean {
  const k = clave.toLowerCase();
  if (PROHIBIDOS.some((p) => k.includes(p))) return false;
  if (CLICK_IDS.has(k)) return conConsentimiento;
  return true;
}

/**
 * Quién quiere enterarse de que la campaña ya está guardada.
 *
 * POR QUÉ HACE FALTA AVISAR A NADIE, QUE ES TODA LA HISTORIA. `withCampaign` se llama al PINTAR el
 * botón de entradas, y `captureCampaign` corre en un EFECTO, o sea después de ese primer pintado.
 * En una navegación interna da igual —la campaña ya estaba guardada de la pantalla anterior—, pero
 * quien aterriza DIRECTO desde un anuncio pinta el botón con el almacén todavía vacío y se queda
 * con la URL pelada para siempre: nada vuelve a repintarlo.
 *
 * Y ése es justo el visitante que importa: el que llega de pagar un clic.
 *
 * Medido en el navegador el 12-ago-2026 contra producción, con `?utm_source=meta&fbclid=…`:
 *   · aterrizar directo en `/buenos-aires` → enlace a la tienda SIN campaña
 *   · aterrizar en `/` y navegar a la ciudad → enlace CON campaña
 * El contraexperimento es lo que descarta que fuera cosa del consentimiento o del propio dato.
 */
const oyentes = new Set<() => void>();

/** Se avisa cuando entra algo nuevo. Devuelve la baja, para el `useSyncExternalStore`. */
export function onCampaignChange(fn: () => void): () => void {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function almacen(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Espejo en memoria de lo último capturado.
 *
 * `sessionStorage` puede fallar —modo privado de Safari, cuota, políticas de empresa— y entonces
 * `readCampaign` devolvía vacío en la misma vista en la que se acababa de capturar la campaña. Con
 * el espejo, un fallo del almacén cuesta la atribución de la SIGUIENTE página, no la de ésta.
 */
let memoria: Campana | null = null;

/** Lo guardado en esta sesión, o vacío. Nunca lanza. */
export function readCampaign(): Campana {
  if (memoria) return memoria;
  const store = almacen();
  if (!store) return {};
  try {
    const raw = store.getItem(CLAVE);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const out: Campana = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string' && v !== '') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Captura la campaña de la URL actual, si la trae.
 *
 * NO PISA lo ya guardado con un vacío: si el visitante llega con campaña y luego
 * navega a una URL limpia, la primera manda. La primera es la que lo trajo.
 */
export function captureCampaign(search: string = window.location.search, conConsentimiento = false): void {
  const query = new URLSearchParams(search);
  const previo = readCampaign();
  const siguiente: Campana = { ...previo };
  let hayNuevo = false;

  // TODO lo que traiga la URL, no una lista cerrada: una lista se queda corta cada vez que una
  // plataforma inventa un parámetro, y se queda corta en silencio. Lo único que no pasa es lo
  // prohibido, lo desmesurado y —sin consentimiento— los identificadores de clic.
  for (const [clave, valor] of query.entries()) {
    if (!valor || previo[clave] !== undefined) continue;
    if (!admisible(clave, conConsentimiento)) continue;
    if (clave.length > 64 || valor.length > MAX_LARGO) continue;
    if (Object.keys(siguiente).length >= MAX_PARAMS) break;
    siguiente[clave] = valor;
    hayNuevo = true;
  }

  if (!hayNuevo) return;
  try {
    almacen()?.setItem(CLAVE, JSON.stringify(siguiente));
  } catch {
    // Sin sitio o sin permiso. Se pierde la atribución de esta visita, que es
    // malo, pero no se rompe la navegación por ello.
  }
  // El aviso va FUERA del `try`: si el almacén falló, la campaña vive igual en memoria para esta
  // vista y el botón debe recogerla. Peor es no propagarla por un fallo de `sessionStorage`.
  memoria = siguiente;
  for (const fn of oyentes) fn();
}

/**
 * Devuelve la URL con la campaña de esta sesión enganchada.
 *
 * **Lo que ya trae la URL de destino gana.** Si algún día un `ticketUrl` lleva su
 * propio `utm_source`, no se le pisa: un parámetro escrito a mano en el dato es
 * una decisión, y la sesión no la sobreescribe.
 *
 * Si la URL no es absoluta y válida se devuelve tal cual: es preferible un
 * enlace que funcione sin atribución a uno roto con ella.
 */
export function withCampaign(url: string): string {
  const campana = readCampaign();
  const claves = Object.keys(campana);
  if (claves.length === 0) return url;

  let destino: URL;
  try {
    destino = new URL(url);
  } catch {
    return url;
  }

  for (const clave of claves) {
    if (destino.searchParams.has(clave)) continue;
    const valor = campana[clave];
    if (valor !== undefined) destino.searchParams.set(clave, valor);
  }
  return destino.toString();
}

/** Solo para las pruebas. */
export function resetCampaignForTests(): void {
  almacen()?.removeItem(CLAVE);
  memoria = null;
  oyentes.clear();
}
